import { AllStatus, DisplayModeSettings } from "@/types/deviceControl";
import { getBoardAddress, checkDeviceHealth, getAIRecordingStatus, getDeviceName, getDeviceSn } from "@/services/api_control";
import { create } from "zustand";

// 设备连接信息接口
export interface DeviceConnectionInfo {
  deviceIP: string;
  serverPort: string;
  connectionId?: string;
  lastConnectTime?: number;
}

// WebSocket录制状态信息
export interface DeviceRecordingStatus {
  isRecording: boolean;
  isPaused?: boolean; // 是否暂停
  streamName?: string;
  startTime?: number;
  duration?: number; // 录制时长（秒）
}

// 设备状态信息
export interface DeviceHealthStatus {
  status: 'healthy' | 'unhealthy';
  services: {
    websocket: boolean;
    ipc: boolean;
    ai_process: boolean;
  };
  lastCheckTime?: number;
}

interface DeviceState {
  // 连接信息
  connectionInfo: DeviceConnectionInfo | null;
  // 录制状态
  recordingStatus: DeviceRecordingStatus | null;
  // 健康状态
  healthStatus: DeviceHealthStatus | null;
  // 连接状态
  isConnected: boolean;
  // WebSocket连接状态
  wsConnected: boolean;
  // 显示模式
  displayMode: DisplayModeSettings;
  // 设备名称
  deviceName: string;
}

// 设备连接结果类型
export interface DeviceConnectResult {
  success: boolean;
  deviceIP?: string;
  isRecording?: boolean;
  streamName?: string;
  errorMsg?: string;
}

interface DeviceStore extends DeviceState {
  // 设置连接信息
  setConnectionInfo: (info: DeviceConnectionInfo) => void;
  // 更新录制状态
  updateRecordingStatus: (status: Partial<DeviceRecordingStatus>) => void;
  // 更新健康状态
  updateHealthStatus: (status: DeviceHealthStatus) => void;
  // 设置WebSocket连接状态
  setWSConnected: (connected: boolean) => void;
  // 获取设备地址
  getDeviceAddress: () => string | null;
  // 设置显示模式
  setDisplayMode: (mode: DisplayModeSettings) => void;
  // 设置设备名称
  setDeviceName: (name: string) => void;
  // 清空所有状态
  resetStore: () => void;
  // 更新所有状态
  updateAllStatus: (status: AllStatus) => void;
  // 检查设备是否可用
  isDeviceAvailable: () => boolean;
  // 通过IP地址连接设备（通用方法）
  connectDeviceByIP: (deviceIP: string, port?: string) => Promise<DeviceConnectResult>;
  // 通过连接码连接设备（整合连接逻辑）
  connectDeviceByCode: (code: string) => Promise<DeviceConnectResult>;
  // 获取并存储设备名称
  fetchAndStoreDeviceName: () => Promise<void>;
  // 获取设备SN
  fetchDeviceSn: () => Promise<string>;
}

const initialState: DeviceState = {
  connectionInfo: null,
  recordingStatus: null,
  healthStatus: null,
  isConnected: false,
  wsConnected: false,
  displayMode: {
    mode: 'original'
  },
  deviceName: ''
};

const deviceStore = create<DeviceStore>((set, get) => ({
  ...initialState,

  setConnectionInfo: (info) => {
    set({
      connectionInfo: {
        ...info,
        lastConnectTime: Date.now()
      },
      isConnected: true
    });
  },

  updateRecordingStatus: (status) => {
    const currentStatus = get().recordingStatus;
    set({
      recordingStatus: {
        isRecording: false, // 默认值
        ...currentStatus,
        ...status
      }
    });
  },

  updateHealthStatus: (status) => {
    set({
      healthStatus: {
        ...status,
        lastCheckTime: Date.now()
      }
    });
  },

  setWSConnected: (connected) => {
    set({ wsConnected: connected });
  },

  getDeviceAddress: () => {
    const { connectionInfo } = get();
    if (!connectionInfo) return null;
    console.log("connectionInfo", connectionInfo);
    return `http://${connectionInfo.deviceIP}:${connectionInfo.serverPort}`;
  },

  setDisplayMode: (mode) => {
    set({ displayMode: mode });
  },

  setDeviceName: (name) => {
    set({ deviceName: name });
  },

  updateAllStatus: (status) => {
    set({
      displayMode: status.settings.displayMode,
      recordingStatus: {
        isRecording: status.recording.status === 'recording',
        isPaused: status.recording.status === 'paused',
        streamName: status.recording.streamName,
        duration: status.recording.duration,
      }
    });
  },

  resetStore: () => {
    set(initialState);
  },

  isDeviceAvailable: () => {
    const { connectionInfo, healthStatus, isConnected } = get();
    return !!(
      connectionInfo &&
      isConnected &&
      healthStatus?.status === 'healthy'
    );
  },

  // 通过IP地址连接设备（通用方法）
  connectDeviceByIP: async (deviceIP: string, port: string = '2998') => {
    try {
      console.log(`尝试连接设备 IP: ${deviceIP}:${port}`);

      // 1. 健康检测
      const healthResult = await checkDeviceHealth(deviceIP, port);
      if (!healthResult.success || !healthResult.healthy) {
        return {
          success: false,
          errorMsg: healthResult.error || "连接失败，请检查网络后重试"
        };
      }
      console.log('✓ 设备健康检测通过:', healthResult.data);

      // 2. 检测 AI 会记状态
      const statusResult = await getAIRecordingStatus(deviceIP, port);
      if (!statusResult.success) {
        return {
          success: false,
          errorMsg: statusResult.error || "获取AI会记状态失败"
        };
      }
      console.log('AI会记状态:', statusResult.data);

      // 3. 存储设备连接信息到 Store
      set({
        connectionInfo: {
          deviceIP,
          serverPort: port,
          lastConnectTime: Date.now()
        },
        isConnected: true
      });

      // 更新设备健康状态
      if (healthResult.data) {
        set({
          healthStatus: {
            ...healthResult.data,
            lastCheckTime: Date.now()
          }
        });
      }

      // 更新录制状态
      const currentStatus = get().recordingStatus;
      set({
        recordingStatus: {
          ...currentStatus,
          isRecording: statusResult.data.isRecording,
          streamName: statusResult.data.streamName
        }
      });

      // 4. 返回成功结果
      return {
        success: true,
        deviceIP,
        isRecording: statusResult.data.isRecording,
        streamName: statusResult.data.streamName
      };
    } catch (error) {
      console.error('IP 连接失败:', error);
      let errorMsg = "连接设备失败";
      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          errorMsg = "连接超时，请检查网络";
        } else if (error.message.includes('fetch')) {
          errorMsg = "网络连接失败";
        } else {
          errorMsg = '连接超时，请检查网络';
        }
      }
      return { success: false, errorMsg };
    }
  },

  // 通过连接码连接设备
  connectDeviceByCode: async (code: string) => {
    try {
      // 1. 获取终端设备IP信息
      const res = await getBoardAddress(code);
      if (!res?.reportData) {
        return { success: false, errorMsg: "设备不在线，请检查后再试" };
      }

      // 2. 收集所有可用IP地址（有线优先，然后无线）
      const { networkInfo, serverPort } = res.reportData;
      const candidateIPs: Array<{ ip: string; interfaceName: string; type: 'wired' | 'wireless' }> = [];

      // 收集有线网络IP
      if (networkInfo.wired && networkInfo.wired.length > 0) {
        for (const wiredInterface of networkInfo.wired) {
          if (wiredInterface.address) {
            candidateIPs.push({
              ip: wiredInterface.address,
              interfaceName: wiredInterface.name,
              type: 'wired'
            });
          }
        }
      }

      // 收集无线网络IP
      if (networkInfo.wireless && networkInfo.wireless.length > 0) {
        for (const wirelessInterface of networkInfo.wireless) {
          if (wirelessInterface.address) {
            candidateIPs.push({
              ip: wirelessInterface.address,
              interfaceName: wirelessInterface.name,
              type: 'wireless'
            });
          }
        }
      }

      if (candidateIPs.length === 0) {
        return { success: false, errorMsg: "设备不在线，请检查后再试" };
      }

      // 3. 遍历所有IP进行连接尝试，使用第一个连接成功的IP
      for (const candidate of candidateIPs) {
        console.log(`尝试连接 ${candidate.type} IP:`, candidate.ip, '接口:', candidate.interfaceName);
        const result = await get().connectDeviceByIP(candidate.ip, serverPort || '2998');

        if (result.success) {
          console.log(`✓ 连接成功，使用 ${candidate.type} IP:`, candidate.ip);
          return result;
        } else {
          console.log(`✗ ${candidate.type} IP ${candidate.ip} 连接失败:`, result.errorMsg || '设备状态异常');
        }
      }

      // 所有IP都连接失败
      return { success: false, errorMsg: "请保证跟设备在同一网络内" };
    } catch (error) {
      console.error('控制连接失败:', error);
      let errorMsg = "连接终端设备失败";

      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          errorMsg = "连接超时，请检查网络";
        } else if (error.message.includes('fetch')) {
          errorMsg = "网络连接失败";
        } else {
          errorMsg = '连接超时，请检查网络';
        }
      }

      return { success: false, errorMsg };
    }
  },

  // 获取并存储设备名称
  fetchAndStoreDeviceName: async () => {
    try {
      const connectionInfo = get().connectionInfo;
      if (!connectionInfo) {
        console.warn('[DeviceStore] 无法获取设备名称：连接信息不存在');
        return;
      }

      const result = await getDeviceName(connectionInfo.deviceIP, connectionInfo.serverPort);
      if (result.success && result.deviceName) {
        console.log('[DeviceStore] 设备名称：', result.deviceName);
        set({ deviceName: result.deviceName });
      }
    } catch (error) {
      console.error('[DeviceStore] 获取设备名称失败：', error);
    }
  },

  // 获取设备SN
  fetchDeviceSn: async (): Promise<string> => {
    try {
      const connectionInfo = get().connectionInfo;
      if (!connectionInfo) {
        console.warn('[DeviceStore] 无法获取设备SN：连接信息不存在');
        return '';
      }

      const result = await getDeviceSn(connectionInfo.deviceIP, connectionInfo.serverPort);
      if (result.success && result.deviceSn) {
        console.log('[DeviceStore] 设备SN：', result.deviceSn);
        return result.deviceSn;
      }
      return '';
    } catch (error) {
      console.error('[DeviceStore] 获取设备SN失败：', error);
      return '';
    }
  },
}));

export default deviceStore;
